import type { KYCStates } from '@/queries/kyc/kyc.schema';
import type { VerificationType } from '@/queries/verifications/verifications.schema';
import { VerificationTypeEnum } from '@/queries/verifications/verifications.schema';

type VideoSource = { src: string; type: string };

type KycStateModalMessages = IntlMessages['kyc']['kyc-state-modal'];
type TitleKey = `title.${keyof KycStateModalMessages['title']}`;
type DescriptionKey = `description.${keyof KycStateModalMessages['description']}`;

type StateConfig = {
  videoSources: VideoSource[];
  titleKey: TitleKey;
  descriptionKey: DescriptionKey;
  singleButton?: boolean;
};

export const DEFAULT_SOURCES: VideoSource[] = [
  { src: '/videos/global-state/tiger-card-states/verification-card-default-ios.mov', type: 'video/quicktime' },
  { src: '/videos/global-state/tiger-card-states/verification-card-default.webm', type: 'video/webm' },
];

const SUCCESS_SOURCES: VideoSource[] = [
  { src: '/videos/global-state/tiger-card-states/success/verification-card-success-ios.mov', type: 'video/quicktime' },
  { src: '/videos/global-state/tiger-card-states/success/verification-card-success.webm', type: 'video/webm' },
];

const REJECTED_SOURCES: VideoSource[] = [
  { src: '/videos/global-state/tiger-card-states/failed/verification-card-failed-ios.mov', type: 'video/quicktime' },
  { src: '/videos/global-state/tiger-card-states/failed/verification-card-failed.webm', type: 'video/webm' },
];

const KYC_STATE_CONFIG: Partial<Record<KYCStates, StateConfig>> = {
  'approved': { videoSources: SUCCESS_SOURCES, titleKey: 'title.approved', descriptionKey: 'description.approved', singleButton: true },
  'rejected': { videoSources: REJECTED_SOURCES, titleKey: 'title.rejected', descriptionKey: 'description.rejected' },
  'resubmission-requested': { videoSources: REJECTED_SOURCES, titleKey: 'title.resubmission-requested', descriptionKey: 'description.resubmission-requested' },
};

const DOCS_STATE_CONFIG: Partial<Record<KYCStates, StateConfig>> = {
  'approved': { videoSources: SUCCESS_SOURCES, titleKey: 'title.additional-documents-approved', descriptionKey: 'description.additional-documents-approved', singleButton: true },
  'rejected': { videoSources: REJECTED_SOURCES, titleKey: 'title.additional-documents-rejected', descriptionKey: 'description.additional-documents-rejected' },
  'resubmission-requested': { videoSources: DEFAULT_SOURCES, titleKey: 'title.additional-documents-resubmission', descriptionKey: 'description.additional-documents-resubmission' },
  'pending': { videoSources: DEFAULT_SOURCES, titleKey: 'title.additional-documents-under-review', descriptionKey: 'description.additional-documents-under-review' },
  'not-submitted': { videoSources: DEFAULT_SOURCES, titleKey: 'title.additional-documents-new', descriptionKey: 'description.additional-documents-new' },
  'incomplete': { videoSources: DEFAULT_SOURCES, titleKey: 'title.additional-documents-requested', descriptionKey: 'description.additional-documents-requested' },
};

export const getStateConfig = (
  type: VerificationType,
  state: KYCStates,
): StateConfig | undefined => {
  const config = type === VerificationTypeEnum.DOCS ? DOCS_STATE_CONFIG : KYC_STATE_CONFIG;
  return config[state];
};
